// src/hooks/usePrestations.ts - Version simplifiée sans fallback complexe

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  getDoc,
  increment,
  writeBatch,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { Prestation, Client, ServiceConsumedProduct, PrestationConsumedProduct, PaymentSplit } from '../types/entities';
import { getPrimaryPaymentMethod } from '../utils/paymentUtils';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { calculateLoyaltyBonus } from '../constants/loyalty';
import { useAuth } from '../contexts/AuthContext';
import {
  getBusinessConfiguration,
  getSalesSiteUiLabels,
  isLoyaltyEnabled,
} from '../types/business';
import { getProductStockAtSite } from '../utils/productStock';
import {
  marketingRevenueForPackAwareLine,
  shouldApplyMarketingPromotionForLine,
} from '../utils/catalogPacks';
import { useApplyPromotion } from './useMarketingCampaigns';
import { usePermissions } from './usePermissions';
import { useDefaultPointOfSaleSite } from './useSites';
import dayjs from 'dayjs';

// ===== HOOKS POUR LES PRESTATIONS =====
export const usePrestations = (filters?: {
  employeeId?: string;
  startDate?: Date;
  endDate?: Date;
  limit?: number;
  includeArchived?: boolean;
  transactionGroupId?: string;
  /** Filtre client-side (mode multi-lieux). */
  siteId?: string;
}) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: [ 'prestations', businessId, filters],
    queryFn: async () => {
      console.log('🔍 usePrestations - Début de la requête', { businessId, filters });
      
      if (!businessId) {
        console.warn('Aucun businessId disponible');
        return [];
      }

      try {
        let q;
        
        // Si on filtre par transactionGroupId
        if (filters?.transactionGroupId) {
          q = query(
            collection(db, COLLECTIONS.prestations),
            where('businessId', '==', businessId),
            where('transactionGroupId', '==', filters.transactionGroupId),
            orderBy('transactionIndex', 'asc')
          );
        }
        // STRATÉGIE 1: Si on filtre par employé ET par dates
        else if (filters?.employeeId && (filters?.startDate || filters?.endDate)) {
          q = query(
            collection(db, COLLECTIONS.prestations),
            where('businessId', '==', businessId),
            where('employeeId', '==', filters.employeeId)
          );
          
          if (filters?.startDate) {
            q = query(q, where('date', '>=', Timestamp.fromDate(filters.startDate)));
          }
          
          if (filters?.endDate) {
            q = query(q, where('date', '<=', Timestamp.fromDate(filters.endDate)));
          }
          
          q = query(q, orderBy('date', 'desc'));
          
        }
        // STRATÉGIE 2: Si on filtre seulement par employé
        else if (filters?.employeeId) {
          q = query(
            collection(db, COLLECTIONS.prestations),
            where('businessId', '==', businessId),
            where('employeeId', '==', filters.employeeId),
            orderBy('date', 'desc')
          );
        }
        // STRATÉGIE 3: Si on filtre seulement par dates
        else if (filters?.startDate || filters?.endDate) {
          q = query(
            collection(db, COLLECTIONS.prestations),
            where('businessId', '==', businessId),
            orderBy('date', 'desc')
          );
          
          if (filters?.startDate) {
            q = query(q, where('date', '>=', Timestamp.fromDate(filters.startDate)));
          }
          
          if (filters?.endDate) {
            q = query(q, where('date', '<=', Timestamp.fromDate(filters.endDate)));
          }
        }
        // STRATÉGIE 4: Filtre par business seulement
        else {
          q = query(
            collection(db, COLLECTIONS.prestations),
            where('businessId', '==', businessId),
            orderBy('date', 'desc')
          );
        }
        
        if (filters?.limit) {
          q = query(q, limit(filters.limit));
        }
        
        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'usePrestations - Main query');
          throw error;
        });
        
        let prestations = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            businessId: data.businessId,
            employeeId: data.employeeId,
            employeeName: data.employeeName,
            serviceId: data.serviceId,
            serviceName: data.serviceName,
            clientId: data.clientId,
            clientName: data.clientName,
            price: data.price,
            finalPrice: data.finalPrice,
            loyaltyAmountUsed: data.loyaltyAmountUsed,
            loyaltyBonusGranted: data.loyaltyBonusGranted,
            paymentMethod: data.paymentMethod,
            paymentSplits: data.paymentSplits as PaymentSplit[] | undefined, // ✅ NOUVEAU : Récupérer paymentSplits depuis Firestore
            notes: data.notes,
            isArchived: data.isArchived,
            archiveReason: data.archiveReason,
            promotionId: data.promotionId,
            promotionDiscount: data.promotionDiscount,
            listUnitPrice: data.listUnitPrice,
            commercialDiscount: data.commercialDiscount || 0,
            commercialDiscountReason: data.commercialDiscountReason,
            passDiscountTotal: data.passDiscountTotal || 0,
            passDiscountLabel: data.passDiscountLabel,
            date: data.date?.toDate(),
            archivedAt: data.archivedAt?.toDate(),
            transactionGroupId: data.transactionGroupId,
            transactionIndex: data.transactionIndex,
            receiptNumber: data.receiptNumber,
            
            // ✅ NOUVEAUX : Champs TVA
            taxRateId: data.taxRateId,
            taxRateName: data.taxRateName,
            taxRate: data.taxRate,
            subtotalExcludingTax: data.subtotalExcludingTax,
            totalTaxAmount: data.totalTaxAmount,
            subtotalIncludingTax: data.subtotalIncludingTax,
            
            // ✅ NOUVEAUX : Champs référent
            referentId: data.referentId,
            referentName: data.referentName,
            
            // ✅ NOUVEAU : Produits consommés
            consumedProducts: data.consumedProducts ? data.consumedProducts.map((cp: any) => ({
              productId: cp.productId,
              productName: cp.productName,
              quantity: cp.quantity,
              unitCost: cp.unitCost,
              stockBefore: cp.stockBefore,
              stockAfter: cp.stockAfter,
              siteId: cp.siteId,
              siteName: cp.siteName,
            })) : undefined,
            
            // ✅ NOUVEAU : Champs pour plan de paiement
            paymentPlanId: data.paymentPlanId,
            paymentStatus: data.paymentStatus,
            amountPaid: data.amountPaid,
            amountRemaining: data.amountRemaining,
            isDelivered: data.isDelivered,
            deliveredAt: data.deliveredAt?.toDate(),
            siteId: data.siteId,
            siteName: data.siteName,

            ...(data.soldAsPack
              ? {
                  packId: data.packId,
                  packInstanceId: data.packInstanceId,
                  packName: data.packName,
                  packPrice: data.packPrice,
                  packComponentIndex: data.packComponentIndex,
                  soldAsPack: true as const,
                }
              : {}),
          };
        }) as Prestation[];
        
        // Filtrer les prestations archivées si nécessaire
        if (!filters?.includeArchived) {
          prestations = prestations.filter(p => !p.isArchived);
        }

        if (filters?.siteId) {
          const siteId = filters.siteId;
          prestations = prestations.filter(
            (p) =>
              p.siteId === siteId ||
              p.consumedProducts?.some((cp) => cp.siteId === siteId)
          );
        }
        
        // Tri selon le contexte
        if (filters?.transactionGroupId) {
          // Pour une transaction spécifique, trier par index
          prestations.sort((a, b) => (a.transactionIndex || 0) - (b.transactionIndex || 0));
        } else {
          // Pour l'historique général, trier par date décroissante
          prestations.sort((a, b) => {
            const dateA = dayjs(a.date);
            const dateB = dayjs(b.date);
            return dateB.valueOf() - dateA.valueOf();
          });
        }
        
        console.log('✅ usePrestations - Prestations récupérées:', prestations.length, prestations.map(p => ({ id: p.id, date: p.date, serviceName: p.serviceName })));
        return prestations;
        
      } catch (error) {
        console.error('Erreur lors de la récupération des prestations:', error);
        throw error;
      }
    },
    enabled: !!businessId && (permissions?.canViewPrestations ?? false),
  });
};

export const useAddPrestation = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  const businessConfig = getBusinessConfiguration(currentBusiness);
  const defaultPosSite = useDefaultPointOfSaleSite(businessId);
  const applyPromotionMutation = useApplyPromotion();
  
  return useMutation({
    mutationFn: async (prestation: Omit<Prestation, 'id' | 'businessId'>) => {
      if (!businessId) {
        throw new Error('Business non défini');
      }

      // ✅ Vérifier que l'employee est actif
      if (prestation.employeeId) {
        const employeeRef = doc(db, COLLECTIONS.employees, prestation.employeeId);
        const employeeSnap = await getDoc(employeeRef);
        if (!employeeSnap.exists()) {
          throw new Error('Employé introuvable');
        }
        const employeeData = employeeSnap.data();
        if (employeeData.isActive !== true) {
          throw new Error('Cet employé est inactif. Impossible d\'enregistrer une prestation.');
        }
      }

      let loyaltyBonusGranted = 0;
      let shouldUpdateClientLoyalty = false;
      
      // ✅ NOUVEAU : Vérifier si la cagnotte est activée avant de calculer
      const loyaltyEnabled = isLoyaltyEnabled(currentBusiness);
      
      // SÉCURITÉ : vérifier que le client appartient au même business (si en cache)
      if (loyaltyEnabled && prestation.clientId) {
        const finalPrice = prestation.finalPrice || prestation.price;
        loyaltyBonusGranted = calculateLoyaltyBonus(finalPrice, currentBusiness);
        shouldUpdateClientLoyalty = true;
        try {
          const clientDoc = await getDoc(doc(db, COLLECTIONS.clients, prestation.clientId));
          if (clientDoc.exists()) {
            const client = clientDoc.data() as Client;
            if (client.businessId !== businessId) {
              throw new Error('Le client n\'appartient pas à cet business');
            }
          }
          // Si client pas en cache (offline), on garde shouldUpdateClientLoyalty = true
          // et on fera un increment() plus bas pour que la cagnotte soit mise à jour au sync
        } catch (e) {
          // En offline getDoc peut échouer ou ne pas avoir le client en cache : on garde shouldUpdateClientLoyalty
          if (e instanceof Error && e.message.includes('n\'appartient pas')) throw e;
        }
      }
      
      // ✅ NOUVEAU : Gérer paymentSplits et paymentMethod (rétrocompatibilité)
      let finalPaymentMethod = prestation.paymentMethod;
      let finalPaymentSplits: PaymentSplit[] | undefined = prestation.paymentSplits;

      // Si paymentSplits est présent, l'utiliser et calculer paymentMethod principal
      if (prestation.paymentSplits && prestation.paymentSplits.length > 0) {
        finalPaymentSplits = prestation.paymentSplits;
        finalPaymentMethod = getPrimaryPaymentMethod(prestation.paymentSplits) || prestation.paymentMethod;
      } else if (prestation.paymentMethod) {
        // Fallback : si seulement paymentMethod est présent, le conserver
        finalPaymentMethod = prestation.paymentMethod;
        finalPaymentSplits = undefined;
      }

      const cleanedPrestation = {
        ...prestation,
        businessId,
        finalPrice: prestation.finalPrice || prestation.price,
        loyaltyAmountUsed: prestation.loyaltyAmountUsed || 0,
        notes: prestation.notes || '',
        date: Timestamp.fromDate(prestation.date),
        loyaltyBonusGranted,
        receiptNumber: prestation.receiptNumber,
        paymentMethod: finalPaymentMethod, // Conservé pour rétrocompatibilité
        ...(finalPaymentSplits && finalPaymentSplits.length > 0 ? { paymentSplits: finalPaymentSplits } : {}), // ✅ NOUVEAU : Sauvegarder paymentSplits si présent
        ...(prestation.clientId ? { clientId: prestation.clientId } : {}),
        isArchived: false,
        // ✅ NOUVEAUX : Champs référent (TVA déjà dans ...prestation)
        ...(prestation.referentId && prestation.referentName && {
          referentId: prestation.referentId,
          referentName: prestation.referentName,
        }),
        // Inclure les données de promotion
        ...(prestation.promotionId && {
          promotionId: prestation.promotionId,
          promotionDiscount: prestation.promotionDiscount ?? 0,
        }),
        ...(prestation.listUnitPrice != null &&
          prestation.listUnitPrice > 0 && { listUnitPrice: prestation.listUnitPrice }),
        ...(prestation.commercialDiscount != null &&
          prestation.commercialDiscount > 0 && {
            commercialDiscount: prestation.commercialDiscount,
          }),
        ...(prestation.commercialDiscountReason?.trim() && {
          commercialDiscountReason: prestation.commercialDiscountReason.trim(),
        }),
        ...(prestation.passDiscountTotal != null &&
          prestation.passDiscountTotal > 0 && {
            passDiscountTotal: prestation.passDiscountTotal,
          }),
        ...(prestation.passDiscountLabel?.trim() && {
          passDiscountLabel: prestation.passDiscountLabel.trim(),
        }),
        // Champs transaction (receiptNumber déjà défini ci-dessus)
        ...(prestation.transactionGroupId != null && {
          transactionGroupId: prestation.transactionGroupId,
          transactionIndex: prestation.transactionIndex,
        }),
        // ✅ NOUVEAU : Champs pour plan de paiement (après ...prestation pour éviter l'écrasement)
        // Toujours inclure ces champs s'ils sont définis (même si 0)
        ...(prestation.paymentPlanId !== undefined && prestation.paymentPlanId !== null && {
          paymentPlanId: prestation.paymentPlanId,
        }),
        ...(prestation.paymentStatus !== undefined && prestation.paymentStatus !== null && {
          paymentStatus: prestation.paymentStatus,
        }),
        // ✅ IMPORTANT : Toujours inclure amountPaid et amountRemaining s'ils sont présents dans prestation
        // (même si 0, car 0 est une valeur valide pour un plan de paiement)
        ...(('amountPaid' in prestation) && {
          amountPaid: prestation.amountPaid ?? 0,
        }),
        ...(('amountRemaining' in prestation) && {
          amountRemaining: prestation.amountRemaining ?? 0,
        }),
        ...(prestation.isDelivered !== undefined && prestation.isDelivered !== null && {
          isDelivered: prestation.isDelivered,
        }),
        ...(prestation.deliveredAt && {
          deliveredAt: Timestamp.fromDate(prestation.deliveredAt),
        }),
        ...(prestation.siteId &&
          prestation.siteName && {
            siteId: prestation.siteId,
            siteName: prestation.siteName,
          }),
      };
      
      // ✅ NOUVEAU : Gérer les produits consommés par la prestation (mode direct)
      const consumedProductsData: PrestationConsumedProduct[] = [];
      const stockMovementRefs: any[] = [];
      let shouldCreateManufacturingOrder = false;
      let manufacturingTemplateLines: { productId: string; productName: string; quantity: number; siteId?: string }[] =
        [];
      
      // Récupérer le service pour vérifier s'il a des produits consommés
      const serviceRef = doc(db, COLLECTIONS.services, prestation.serviceId);
      const serviceSnap = await getDoc(serviceRef);
      
      if (serviceSnap.exists()) {
        const serviceData = serviceSnap.data();
        const serviceConsumedProducts = (serviceData.consumedProducts as ServiceConsumedProduct[] | undefined)?.filter(
          (p) => p && String(p.productId ?? '').trim() && Number(p.quantity) > 0
        );
        const templateLines = Array.isArray((serviceData.manufacturingTemplate as any)?.defaultLines)
          ? (serviceData.manufacturingTemplate as any).defaultLines
          : [];
        const inferredMode =
          serviceData.stockConsumptionMode ??
          (serviceConsumedProducts && serviceConsumedProducts.length > 0
            ? 'direct'
            : templateLines.length > 0
              ? 'manufacturing_order'
              : 'none');

        if (inferredMode === 'manufacturing_order') {
          shouldCreateManufacturingOrder = true;
          const template = serviceData.manufacturingTemplate as any;
          const defaultLines = Array.isArray(template?.defaultLines) ? template.defaultLines : [];
          manufacturingTemplateLines = defaultLines
            .map((l: any) => ({
              productId: String(l?.productId ?? '').trim(),
              productName: String(l?.productName ?? '').trim(),
              quantity: Number(l?.quantity ?? 0),
              siteId: l?.siteId ? String(l.siteId).trim() : undefined,
              siteName: l?.siteName ? String(l.siteName).trim() : undefined,
            }))
            .filter((l: any) => l.productId && Number.isFinite(l.quantity) && l.quantity > 0);

          const lineMissingSite = manufacturingTemplateLines.find(
            (l: any) => !l.siteId || !String(l.siteName ?? '').trim()
          );
          if (lineMissingSite && manufacturingTemplateLines.length > 0) {
            throw new Error(
              `Le modèle de fabrication du service « ${serviceData.name ?? 'prestation'} » est incomplet : chaque ligne doit avoir un lieu (identifiant et libellé). Mettez à jour la fiche prestation avant de vendre cette fabrication.`
            );
          }
        }

        // ✅ Règle métier : une prestation qui déclenche une fabrication doit être rattachée à un client identifié.
        // (On refuse de créer un OF sans client, même si l'UI a laissé passer.)
        if (shouldCreateManufacturingOrder && !prestation.clientId) {
          throw new Error(
            'Cette prestation déclenche une fabrication. Veuillez sélectionner un client avant de valider la transaction.'
          );
        }
        const resolvedSiteId = defaultPosSite?.id;
        const resolvedSiteName = defaultPosSite?.name;
        
        if (!shouldCreateManufacturingOrder && serviceConsumedProducts && serviceConsumedProducts.length > 0) {
          if (!resolvedSiteId || !resolvedSiteName) {
            throw new Error(getSalesSiteUiLabels(currentBusiness).missingSalesSite);
          }
          // Utiliser un batch pour garantir la cohérence
          const batch = writeBatch(db);
          
          // Vérifier et préparer la déduction de stock pour chaque produit
          for (const consumedProduct of serviceConsumedProducts) {
            const productRef = doc(db, COLLECTIONS.products, consumedProduct.productId);
            const productSnap = await getDoc(productRef);
            
            if (!productSnap.exists()) {
              throw new Error(`Le produit "${consumedProduct.productName}" associé à ce service n'existe plus. Veuillez mettre à jour la configuration du service.`);
            }
            
            const productData = productSnap.data();
            
            // Vérifier que le produit appartient au même business
            if (productData.businessId !== businessId) {
              throw new Error(`Le produit "${consumedProduct.productName}" n'appartient pas à votre business.`);
            }
            
            // Vérifier que le produit est actif
            if (productData.isActive !== true) {
              throw new Error(`Le produit "${consumedProduct.productName}" est désactivé. Veuillez le réactiver ou mettre à jour la configuration du service.`);
            }
            
            // Vérifier le stock disponible
            const stockBySite = (productData.stockBySite as Record<string, number>) || {};
            const availableAtSite = getProductStockAtSite(
              { currentStock: productData.currentStock, stockBySite },
              resolvedSiteId
            );
            if (availableAtSite < consumedProduct.quantity) {
              throw new Error(
                `Stock insuffisant pour le produit "${consumedProduct.productName}". Stock disponible: ${availableAtSite}, quantité requise: ${consumedProduct.quantity}`
              );
            }
            
            // Préparer la déduction de stock dans le batch
            const productUpdate: Record<string, unknown> = {
              currentStock: increment(-consumedProduct.quantity),
              updatedAt: Timestamp.now(),
            };
            if (resolvedSiteId) {
              productUpdate.stockBySite = {
                ...stockBySite,
                [resolvedSiteId]: Math.max(0, availableAtSite - consumedProduct.quantity),
              };
            }
            batch.update(productRef, productUpdate as any);
            
            // Créer une référence pour le mouvement de stock (sera créé après la prestation)
            const stockMovementRef = doc(collection(db, COLLECTIONS.stockMovements));
            stockMovementRefs.push({
              ref: stockMovementRef,
              productId: consumedProduct.productId,
              productName: consumedProduct.productName,
              quantity: consumedProduct.quantity,
              currentStock: productData.currentStock || 0,
              siteId: resolvedSiteId,
              siteName: resolvedSiteName,
            });
            
            // Enregistrer les données pour la prestation
            consumedProductsData.push({
              productId: consumedProduct.productId,
              productName: consumedProduct.productName,
              quantity: consumedProduct.quantity,
              stockBefore: availableAtSite,
              stockAfter: availableAtSite - consumedProduct.quantity,
              siteId: resolvedSiteId,
              siteName: resolvedSiteName,
            });
          }
          
          // Exécuter le batch pour déduire les stocks
          await batch.commit();
        }
      }
      
      // Ajouter consumedProducts à la prestation si des produits ont été consommés
      if (consumedProductsData.length > 0) {
        cleanedPrestation.consumedProducts = consumedProductsData;
      }
      
      const docRef = await addDoc(collection(db, COLLECTIONS.prestations), cleanedPrestation);
      const prestationId = docRef.id;

      // ✅ NOUVEAU : Créer un ordre de fabrication si le service est en mode fabrication
      if (shouldCreateManufacturingOrder) {
        const now = Timestamp.now();
        await addDoc(collection(db, COLLECTIONS.manufacturingOrders), {
          businessId,
          status: 'draft',
          prestationId,
          ...(prestation.transactionGroupId ? { transactionGroupId: prestation.transactionGroupId } : {}),
          serviceId: prestation.serviceId,
          serviceName: prestation.serviceName,
          ...(prestation.clientId ? { clientId: prestation.clientId } : {}),
          ...(prestation.clientName ? { clientName: prestation.clientName } : {}),
          plannedLines: manufacturingTemplateLines.map((l: any) => ({
            productId: l.productId,
            quantity: Math.max(0, Math.floor(Number(l.quantity))),
            siteId: l.siteId,
            ...(l.siteName ? { siteName: l.siteName } : {}),
          })),
          actualLines: [],
          stockApplied: false,
          createdAt: now,
          updatedAt: now,
        });
      }
      
      // ✅ NOUVEAU : Créer les mouvements de stock avec le prestationId
      if (stockMovementRefs.length > 0) {
        const movementsBatch = writeBatch(db);
        for (const movementData of stockMovementRefs) {
          movementsBatch.set(movementData.ref, {
            businessId,
            productId: movementData.productId,
            productName: movementData.productName,
            type: 'exit',
            quantity: movementData.quantity,
            reason: 'Consommation prestation',
            date: Timestamp.fromDate(prestation.date),
            employeeId: prestation.employeeId,
            employeeName: prestation.employeeName,
            createdAt: Timestamp.now(),
            prestationId: prestationId,
            ...(movementData.siteId && movementData.siteName ? { siteId: movementData.siteId, siteName: movementData.siteName } : {}),
          });
        }
        await movementsBatch.commit();
      }
      
      // Mettre à jour le client : cagnotte (si nécessaire) + dernière visite
      if (prestation.clientId) {
        const clientRef = doc(db, COLLECTIONS.clients, prestation.clientId);
        const updateData: any = {
          lastVisitDate: Timestamp.fromDate(prestation.date),
          updatedAt: Timestamp.now(),
        };
        
        // Mettre à jour la cagnotte si nécessaire
        if (shouldUpdateClientLoyalty) {
          const loyaltyAmountUsed = prestation.loyaltyAmountUsed || 0;
          const loyaltyChange = -loyaltyAmountUsed + loyaltyBonusGranted;
          if (loyaltyChange !== 0) {
            updateData.loyaltyBalance = increment(loyaltyChange);
          }
        }
        
        await updateDoc(clientRef, updateData);
      }
      
      // Si une promotion est utilisée, utiliser le nouveau système
      if (prestation.promotionId) {
        try {
          const promotionRef = doc(db, COLLECTIONS.promotions, prestation.promotionId);
          const promotionSnap = await getDoc(promotionRef);
          
          if (promotionSnap.exists()) {
            const promotionData = promotionSnap.data();
            
            // SÉCURITÉ : Vérifier que la promotion appartient au même business
            if (promotionData.businessId !== businessId) {
              throw new Error('La promotion n\'appartient pas à cet business');
            }

            // Pack : une seule fois par instance (composant index 0) pour éviter inflation CA marketing
            if (shouldApplyMarketingPromotionForLine(prestation)) {
              await applyPromotionMutation.mutateAsync({
                promotionId: prestation.promotionId,
                prestationId: docRef.id,
                revenueAmount: marketingRevenueForPackAwareLine(prestation),
              });
            }
          }
        } catch (promotionError) {
          console.error('Erreur lors de l\'application de la promotion:', promotionError);
          // Ne pas faire échouer la prestation si la promotion échoue
        }
      }
      
      return docRef.id;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [ 'prestations', businessId] });
      
      if (variables.clientId) {
        queryClient.invalidateQueries({ queryKey: [ 'clients', businessId] });
      }
      
      if (variables.promotionId) {
        queryClient.invalidateQueries({ queryKey: ['marketing', 'promotions'] });
        queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns'] });
        queryClient.invalidateQueries({ queryKey: ['marketing', 'dashboard'] });
      }
      
      // ✅ NOUVEAU : Invalider les queries de produits et mouvements de stock si des produits ont été consommés
      // On vérifie si le service a des consumedProducts en récupérant le service
      // Pour simplifier, on invalide toujours (peu coûteux)
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', businessId] });
      queryClient.invalidateQueries({ queryKey: ['manufacturing', 'orders', businessId] });
      
      // Invalider les queries de transaction si c'est une transaction groupée
      if (variables.transactionGroupId) {
        queryClient.invalidateQueries({ 
          queryKey: ['transaction', 'items', businessId, variables.transactionGroupId] 
        });
      }
    },
  });
};

export const useUpdatePrestation = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Prestation> & { id: string }) => {
      const prestationRef = doc(db, COLLECTIONS.prestations, id);
      
      // Seuls certains champs sont modifiables (pas currentBusiness?.id ni les champs transaction)
      const allowedFields = ['date', 'employeeId', 'employeeName', 'paymentMethod', 'notes', 
        'paymentPlanId', 'paymentStatus', 'amountPaid', 'amountRemaining', 'isDelivered', 'deliveredAt'];
      const updateData: any = {};
      
      Object.entries(data).forEach(([key, value]) => {
        if (allowedFields.includes(key)) {
          if (key === 'date' && value) {
            updateData[key] = Timestamp.fromDate(value as Date);
          } else if (key === 'deliveredAt' && value) {
            updateData[key] = Timestamp.fromDate(value as Date);
          } else if (value !== undefined) {
            updateData[key] = value;
          }
        }
      });
      
      await updateDoc(prestationRef, updateData);
      return { id, ...data };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ 'prestations', businessId] });
    },
  });
};

export const useArchivePrestation = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (prestationToArchive: { 
      id: string; 
      employeeId: string; 
      clientId?: string;
      reason: string;
      /** Défaut voided ; superseded pour le flux Corriger. */
      lifecycleStatus?: 'voided' | 'superseded' | 'credited';
      correctionEventId?: string;
      supersededByTransactionGroupId?: string;
      /** Correction vente : échec stock = échec de l’opération (pas de swallow). */
      strictStockRestore?: boolean;
    }) => {
      const prestationRef = doc(db, COLLECTIONS.prestations, prestationToArchive.id);
      const prestationSnap = await getDoc(prestationRef);
      
      if (!prestationSnap.exists()) {
        throw new Error('Prestation introuvable');
      }
      
      const prestation = prestationSnap.data() as Prestation;
      
      // SÉCURITÉ : Vérifier l'appartenance à business
      if (prestation.businessId !== businessId) {
        throw new Error('Cette prestation n\'appartient pas à votre business');
      }

      const lifecycleStatus = prestationToArchive.lifecycleStatus || 'voided';
      const existingEventIds = Array.isArray((prestation as any).correctionEventIds)
        ? ([...(prestation as any).correctionEventIds] as string[])
        : [];
      if (prestationToArchive.correctionEventId) {
        existingEventIds.push(prestationToArchive.correctionEventId);
      }
      
      // Archiver la prestation
      await updateDoc(prestationRef, {
        isArchived: true,
        archivedAt: Timestamp.now(),
        archiveReason: prestationToArchive.reason,
        lifecycleStatus,
        ...(existingEventIds.length
          ? { correctionEventIds: existingEventIds }
          : {}),
        ...(prestationToArchive.supersededByTransactionGroupId
          ? {
              supersededByTransactionGroupId:
                prestationToArchive.supersededByTransactionGroupId,
            }
          : {}),
        notes: prestation.notes 
          ? `${prestation.notes}\n\n--- ARCHIVÉE ---\nRaison: ${prestationToArchive.reason}`
          : `--- ARCHIVÉE ---\nRaison: ${prestationToArchive.reason}`
      });
      
      // ✅ NOUVEAU : Vérifier si la cagnotte est activée avant de restaurer
      const loyaltyEnabled = isLoyaltyEnabled(currentBusiness);
      
      // Ajuster la cagnotte du client si nécessaire (seulement si activée)
      const loyaltyBonusToRemove = prestation.loyaltyBonusGranted || 0;
      const loyaltyUsageToRestore = prestation.loyaltyAmountUsed || 0;
      
      if (loyaltyEnabled && (loyaltyBonusToRemove > 0 || loyaltyUsageToRestore > 0) && prestation.clientId) {
        const clientRef = doc(db, COLLECTIONS.clients, prestation.clientId);
        const clientSnap = await getDoc(clientRef);
        
        if (clientSnap.exists()) {
          const currentClient = clientSnap.data() as Client;
          
          // SÉCURITÉ : Vérifier l'appartenance du client à business
          if (currentClient.businessId === businessId) {
            const newLoyaltyBalance = Math.max(0, 
              (currentClient.loyaltyBalance || 0) + loyaltyUsageToRestore - loyaltyBonusToRemove
            );
            
            await updateDoc(clientRef, {
              loyaltyBalance: newLoyaltyBalance
            });
          }
        }
      }
      
      // Restaurer la promotion selon le usageType
      if (prestation.promotionId) {
        try {
          const promotionRef = doc(db, COLLECTIONS.promotions, prestation.promotionId);
          const promotionSnap = await getDoc(promotionRef);
          
          if (promotionSnap.exists()) {
            const promotionData = promotionSnap.data();
            
            if (promotionData.businessId === businessId) {
              // Récupérer la campagne pour connaître le usageType
              const campaignRef = doc(db, COLLECTIONS.campaigns, promotionData.campaignId);
              const campaignSnap = await getDoc(campaignRef);
              
              if (campaignSnap.exists()) {
                const campaignData = campaignSnap.data();
                const usageType = campaignData.usageType || 'single_use';
                
                console.log(`Archivage prestation: restauration promotion ${prestation.promotionId}, usageType=${usageType}`);
                
                if (usageType === 'single_use') {
                  // Comportement actuel : réactiver la promotion
                  await updateDoc(promotionRef, {
                    used: false,
                    usedAt: null,
                    prestationId: null,
                    active: true,
                    usageCount: Math.max(0, (promotionData.usageCount || 1) - 1),  // Décrémenter
                  });
                } else {
                  // Nouveau comportement : décrémenter seulement le compteur
                  const newUsageCount = Math.max(0, (promotionData.usageCount || 1) - 1);
                  await updateDoc(promotionRef, {
                    usageCount: newUsageCount,
                    // Si plus d'usage, retirer la dernière utilisation
                    ...(newUsageCount === 0 && { lastUsedAt: null }),
                    // La promotion reste active (used: false) car period_based
                  });
                }
                
                // Décrémenter les stats de la campagne
                await updateDoc(campaignRef, {
                  'stats.conversionsCount': increment(-1),
                  'stats.revenueGenerated': increment(-(prestation.finalPrice || prestation.price)),
                });
              }
            }
          }
        } catch (promotionError) {
          console.error('Erreur lors de la restauration de la promotion:', promotionError);
        }
      }
      
      // ✅ NOUVEAU : Restaurer le stock des produits consommés
      if (prestation.consumedProducts && prestation.consumedProducts.length > 0) {
        try {
          const batch = writeBatch(db);
          
          for (const consumedProduct of prestation.consumedProducts) {
            const productRef = doc(db, COLLECTIONS.products, consumedProduct.productId);
            const productSnap = await getDoc(productRef);
            
            if (productSnap.exists()) {
              const productData = productSnap.data();
              if (productData.businessId === businessId) {
                // Restaurer le stock
                const productUpdate: Record<string, unknown> = {
                  currentStock: increment(consumedProduct.quantity),
                  updatedAt: Timestamp.now(),
                };
                if (consumedProduct.siteId) {
                  const stockBySite = (productData.stockBySite as Record<string, number>) || {};
                  const currentAtSite = getProductStockAtSite(
                    { currentStock: productData.currentStock, stockBySite },
                    consumedProduct.siteId
                  );
                  productUpdate.stockBySite = {
                    ...stockBySite,
                    [consumedProduct.siteId]: currentAtSite + consumedProduct.quantity,
                  };
                }
                batch.update(productRef, productUpdate as any);
                
                // Créer un mouvement de stock pour traçabilité
                const movementRef = doc(collection(db, COLLECTIONS.stockMovements));
                batch.set(movementRef, {
                  businessId,
                  productId: consumedProduct.productId,
                  productName: consumedProduct.productName,
                  type: 'adjustment',
                  quantity: consumedProduct.quantity,
                  reason: 'Archivage prestation - restauration stock',
                  reference: `ARCH-PREST-${prestationToArchive.id}`,
                  date: Timestamp.now(),
                  employeeId: prestationToArchive.employeeId,
                  employeeName: prestation.employeeName,
                  notes: `Restauration stock suite à archivage prestation: ${prestationToArchive.reason}`,
                  createdAt: Timestamp.now(),
                  prestationId: prestationToArchive.id,
                  ...(consumedProduct.siteId && consumedProduct.siteName
                    ? { siteId: consumedProduct.siteId, siteName: consumedProduct.siteName }
                    : {}),
                });
              }
            }
          }
          
          await batch.commit();
        } catch (consumedProductsError) {
          console.error('Erreur lors de la restauration du stock des produits consommés:', consumedProductsError);
          if (prestationToArchive.strictStockRestore) {
            throw consumedProductsError instanceof Error
              ? consumedProductsError
              : new Error('Échec restauration stock produits consommés');
          }
          // Legacy archive UI : ne pas faire échouer l'archivage
        }
      }
      
      // ✅ NOUVEAU : Supprimer la référence (référent) si elle existe
      if (prestation.referentId) {
        try {
          const referencesQuery = query(
            collection(db, COLLECTIONS.references),
            where('businessId', '==', businessId),
            where('saleId', '==', prestationToArchive.id),
            where('saleType', '==', 'prestation')
          );
          
          const referencesSnapshot = await getDocs(referencesQuery);
          for (const refDoc of referencesSnapshot.docs) {
            await deleteDoc(doc(db, COLLECTIONS.references, refDoc.id));
          }
        } catch (referenceError) {
          console.error('Erreur lors de la suppression de la référence:', referenceError);
          // Ne pas faire échouer l'archivage si la suppression de la référence échoue
        }
      }
      
      return prestationToArchive;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [ 'prestations', businessId] });
      
      if (data.clientId) {
        queryClient.invalidateQueries({ queryKey: [ 'clients', businessId] });
      }
      
      queryClient.invalidateQueries({ queryKey: ['marketing', 'promotions'] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', businessId] });
      queryClient.invalidateQueries({ queryKey: ['references', businessId] });
    },
  });
};